import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { useToast } from '@givebutter/primitives/toast';
import { faStickyNote } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateTransaction } from 'api/transactions';
import * as EditableInput from 'components/editable-input';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import { useForceRender } from 'customHooks/useForceRender';
import { accountQueryKeys } from 'queries/account-query-keys';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { Transaction } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface DonationInternalNoteForm {
    internalNote: string;
}

const TransactionEditableInternalNote = () => {
    const toast = useToast();
    const { data: transaction } = useCurrentTransactionOrById();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { key, forceRender } = useForceRender();

    const methods = Form.useForm<DonationInternalNoteForm>({
        defaultValues: {
            internalNote: transaction?.internal_note ?? '',
        },
    });

    const { control, path, handleSubmit, resetFormButton } = methods;

    const { mutate: submit } = useMutation({
        mutationFn: (values: DonationInternalNoteForm) =>
            updateTransaction(transaction?.id!, {
                internal_note: values.internalNote,
            }),
        onSuccess: response => {
            toast.success('Successfully updated transaction internal note!');

            queryClient.setQueryData(
                transactionQueryKeys.transaction(accountId, transaction.id),
                (oldData: Transaction) => ({
                    ...oldData,
                    internal_note: response.internal_note,
                }),
            );

            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
            });

            if (transaction.contact) {
                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.contacts(accountId),
                });
            }

            forceRender();
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data?.message ?? 'Failed to update transaction internal note');
            resetFormButton();
        },
    });

    return (
        <Box className={'flex flex-col gap-2 p-4'} key={key}>
            <EditableInput.Root>
                <EditableInput.ReadOnly className={'flex items-start justify-between'}>
                    <div className={'flex flex-col gap-2'}>
                        <div className={'flex items-center gap-2'}>
                            <FontAwesomeIcon
                                className={'text-brand-violet-primary'}
                                icon={faStickyNote}
                            />
                            <p className={'font-medium text-gray-800'}>Internal note</p>
                        </div>
                        {transaction?.internal_note && (
                            <p className={'italic'}>{transaction?.internal_note}</p>
                        )}
                    </div>
                    <EditableInput.ReadOnlyEditTrigger />
                </EditableInput.ReadOnly>
                <EditableInput.Content>
                    <Form.Provider {...methods}>
                        <form
                            onSubmit={handleSubmit(
                                submit as SubmitHandler<DonationInternalNoteForm>,
                            )}
                            className={'contents'}
                        >
                            <Form.Container>
                                <Form.Label name={path('internalNote')} className={'text-gray-800'}>
                                    Internal note
                                </Form.Label>
                                <Form.Textarea
                                    control={control}
                                    name={path('internalNote')}
                                    placeholder={'Type a note'}
                                    rows={8}
                                />
                                <Form.Error name={path('internalNote')} />
                            </Form.Container>
                            <EditableInput.ActionsContainer>
                                <EditableInput.Cancel />
                                <Form.SubmitButton>Save</Form.SubmitButton>
                            </EditableInput.ActionsContainer>
                        </form>
                    </Form.Provider>
                </EditableInput.Content>
            </EditableInput.Root>
        </Box>
    );
};

export { TransactionEditableInternalNote };
